/* ==========================================================================
   OBLIVION — Quantity discounts (customer-facing PREVIEW only)

   READ-ONLY WITH RESPECT TO THE PAGE. This file:
     • never writes to name="quantity", name="id" or any other form field
     • never writes a price into a cart form
     • never intercepts, cancels or submits an add-to-cart
     • never calls a discount API, a CDN or a font host
     • only reads the DOM, does arithmetic, and writes text into its own
       [data-ob-vd] component

   CART MODE is the one exception to "no network". A cart prompt's button
   changes a QUANTITY, and it does that the theme's own way: it puts the new
   quantity into Horizon's cart-line control and dispatches Horizon's own
   quantity-selector:update, so the theme performs the cart change, the
   section morph and the bubble update. Only when that control is missing
   does this file POST /cart/change.js itself, addressing the line by its
   line-item KEY where the server gave one. Even then it sends a quantity and
   nothing else: no price, no discount, no line property, no cart attribute.

   The real discount is a Shopify AUTOMATIC discount, one per tier, scoped to
   the product and set not to combine. Shopify applies it in the cart and
   picks the best qualifying tier itself. This component previews that maths
   on the product page so the shopper can see it before adding. If the tiers
   here and the automatic discounts in the Admin drift apart, the cart is
   right and this box is wrong — the box has no power to change a price.

   The product page is not a normal Horizon product page. GemPages duplicates
   the whole buy-box for desktop, mobile and a sticky bar, so a page carries
   about EIGHT <form action="/cart/add"> elements and ELEVEN name="quantity"
   inputs. Quantity is a hidden <input> inside a <gp-product-quantity> custom
   element, written programmatically (no input/change event guaranteed), and
   GoKwik replaces the payment button with #gokwik-buy-now. Therefore:

     • nothing assumes there is one of anything
     • the quantity source is whichever candidate is actually RENDERED, and
       it switches to whichever one the shopper last touched
     • quantity is observed four ways: input, change, delegated clicks, and a
       MutationObserver on the value attribute
     • the variant is re-read from the hidden name="id" input and from the
       theme's variant:update event, and its price is always looked up afresh
       in the config's variant map — the previous variant's price is never
       reused

   Config: the #ob-vd-config JSON emitted by snippets/oblivion-vd-head.liquid.
   With no config, a malformed config, no tiers, no tier that actually
   discounts, an unavailable variant or a variant with no price, the
   component stays hidden — nothing renders rather than a broken box.

   Every string is drawn from the config. Money is formatted with the shop's
   own money_format string; there is no hardcoded currency symbol anywhere in
   this file.

   Exposes only window.OblivionVD = { version, refresh(), state() }.
   No dependencies. ES5 syntax on purpose (older Android WebViews).
   ========================================================================== */
(function () {
  'use strict';

  /* initialised twice (a duplicated script tag, a section reload that
     re-injects the asset) — the first instance keeps the page */
  if (window.OblivionVD && window.OblivionVD.version) return;

  var VERSION = '1.1.0';
  var READ_DELAY = 0;
  var READ_SETTLE = 140; /* second read: plus/minus controls write the value after the click */
  var DOM_DEBOUNCE = 150;
  var MAX_TIERS = 12;
  var MAX_TIER_QTY = 10000;

  /* ---------------- tiny helpers (never throw) ---------------- */

  function qsa(sel, root) {
    try {
      return Array.prototype.slice.call((root || document).querySelectorAll(sel));
    } catch (e) { return []; }
  }

  function qs(sel, root) {
    try { return (root || document).querySelector(sel); } catch (e) { return null; }
  }

  function isArray(v) {
    return Object.prototype.toString.call(v) === '[object Array]';
  }

  function num(v, fallback) {
    if (typeof v === 'number' && isFinite(v)) return v;
    if (typeof v === 'string' && v !== '') {
      var n = parseFloat(v.replace(/[^0-9eE+.-]/g, ''));
      if (isFinite(n)) return n;
    }
    return fallback;
  }

  function int(v, fallback) {
    var n = num(v, NaN);
    if (!isFinite(n)) return fallback;
    return Math.floor(n);
  }

  function str(v) {
    return typeof v === 'string' ? v.replace(/^\s+|\s+$/g, '') : '';
  }

  function pick(obj, keys) {
    for (var i = 0; i < keys.length; i++) {
      if (obj && obj[keys[i]] !== undefined && obj[keys[i]] !== null && obj[keys[i]] !== '') {
        return obj[keys[i]];
      }
    }
    return undefined;
  }

  /* {n} / {label} placeholders — split/join so every occurrence is replaced */
  function fill(tpl, map) {
    var out = typeof tpl === 'string' ? tpl : '';
    for (var key in map) {
      if (!Object.prototype.hasOwnProperty.call(map, key)) continue;
      out = out.split('{' + key + '}').join(String(map[key]));
    }
    return out;
  }

  function setText(node, value) {
    if (!node) return;
    var next = value === null || value === undefined ? '' : String(value);
    if (node.textContent !== next) node.textContent = next;
  }

  function show(node, visible) {
    if (!node) return;
    var wanted = !visible;
    if (node.hidden !== wanted) node.hidden = wanted;
  }

  function closestEl(el, sel) {
    try {
      if (el && el.closest) return el.closest(sel);
    } catch (e) { /* bad selector on an old engine */ }
    return null;
  }

  /* rendered, not merely present. A hidden <input> inside a visible
     <gp-product-quantity> counts: the input itself has no box, so the walk
     goes up a few levels looking for one. A buy-box hidden with
     display:none (the desktop copy on a phone) has none anywhere. */
  function isRendered(el) {
    if (!el || !el.getClientRects) return false;
    try {
      for (var node = el; node && node !== document.documentElement; node = node.parentElement) {
        var style = window.getComputedStyle(node);
        if (node.hidden || (style.display === 'none' && !(node === el && el.tagName === 'INPUT' && el.type === 'hidden')) || style.visibility === 'hidden') return false;
      }
      if (el.getClientRects().length > 0) return true;
      if (el.tagName === 'INPUT' && el.type === 'hidden') {
        return !!(el.parentElement && el.parentElement.getClientRects().length);
      }
    } catch (e) {}
    return false;
  }

  /* Horizon's desktop quick-add modal clones the whole product-details group.
     A clone must never be treated as a real buy box, and a cloned component
     must never hydrate — two components wired to one id would fight. */
  function isClone(el) {
    return !!(closestEl(el, '#quick-add-modal-content') || closestEl(el, 'quick-add-dialog'));
  }

  function dataLayerPush(payload) {
    try {
      if (window.dataLayer && typeof window.dataLayer.push === 'function') {
        window.dataLayer.push(payload);
      }
    } catch (e) { /* analytics must never break the component */ }
  }

  /* ---------------- config ---------------- */

  function readConfig() {
    var raw;
    try {
      var el = document.getElementById('ob-vd-config');
      if (!el) return null;
      raw = JSON.parse(el.textContent || '{}');
    } catch (e) { return null; }

    if (!raw || typeof raw !== 'object' || isArray(raw)) return null;
    if (raw.enabled === false) return null;
    return raw;
  }

  /* The tiers metafield may be a text-typed field, which Liquid cannot parse,
     so it arrives verbatim as a JSON string in tiersRaw. Parsing it here
     rather than in Liquid means a malformed metafield can never break the
     surrounding config — at worst it is ignored. */
  function parseRawTiers(value) {
    if (typeof value !== 'string' || value === '') return null;
    var parsed;
    try { parsed = JSON.parse(value); } catch (e) { return null; }
    if (isArray(parsed)) return parsed;
    if (parsed && isArray(parsed.tiers)) return parsed.tiers;
    return null;
  }

  function normalizeTiers(list, texts) {
    if (!isArray(list) || !list.length) return [];
    var out = [];
    var i;
    for (i = 0; i < list.length; i++) {
      var t = list[i];
      if (!t || typeof t !== 'object' || isArray(t)) continue;

      var q = int(pick(t, ['minQuantity', 'min_quantity', 'quantity', 'qty', 'min']), 0);
      if (!(q >= 1) || q === 3 || q > MAX_TIER_QTY) continue;

      var type = String(pick(t, ['discountType', 'discount_type', 'type']) || 'percent').toLowerCase();
      type = (type.indexOf('amount') > -1 || type.indexOf('fixed') > -1 || type.indexOf('money') > -1)
        ? 'amount'
        : 'percent';

      var v = num(pick(t, ['discountValue', 'discount_value', 'percent', 'percentage', 'value']), 0);
      if (!(v > 0)) v = 0;
      if (type === 'percent' && v > 100) v = 100;

      var label = str(t.label);
      if (!label) label = fill(texts.tierLabel, { n: q }) || String(q);

      out.push({
        minQuantity: q,
        discountType: type,
        discountValue: v,
        label: label,
        badge: str(t.badge)
      });
    }

    out.sort(function (a, b) { return a.minQuantity - b.minQuantity; });

    /* two tiers at the same minimum quantity cannot both be shown; the later
       one in the source wins, which is also what a merchant would expect */
    var dedup = [];
    for (i = 0; i < out.length; i++) {
      var prev = dedup.length ? dedup[dedup.length - 1] : null;
      if (prev && prev.minQuantity === out[i].minQuantity) dedup[dedup.length - 1] = out[i];
      else dedup.push(out[i]);
    }
    return dedup.slice(0, MAX_TIERS);
  }

  var cfg = readConfig();
  if (!cfg) return;

  var T = (cfg.texts && typeof cfg.texts === 'object' && !isArray(cfg.texts)) ? cfg.texts : {};

  /* Tier resolution order, the same order the head snippet documents: the
     product metafield first, Theme settings second. A metafield that parses
     to nothing usable falls through to the settings rather than blanking the
     component. */
  var TIERS = normalizeTiers(parseRawTiers(cfg.tiersRaw), T);
  if (!TIERS.length) TIERS = normalizeTiers(cfg.tiers, T);

  /* a table of one row at 0% is not an offer */
  var hasDiscount = false;
  for (var ti = 0; ti < TIERS.length; ti++) {
    if (TIERS[ti].discountValue > 0) { hasDiscount = true; break; }
  }

  var VARIANTS = (cfg.variants && typeof cfg.variants === 'object' && !isArray(cfg.variants)) ? cfg.variants : {};

  var hasVariants = false;
  for (var vk in VARIANTS) {
    if (Object.prototype.hasOwnProperty.call(VARIANTS, vk)) { hasVariants = true; break; }
  }

  /* Two independent modes off one config and one script.

     PRODUCT MODE previews the maths next to the buy box. It needs resolved
     tiers, a tier that actually discounts, and the variant price map — on a
     cart-only page (the drawer renders on every page of this theme) none of
     those are emitted, so it simply stays off.

     CART MODE drives quantity from the cart line prompts. Every figure there
     is server-rendered per line by snippets/oblivion-vd-cart-item.liquid, so
     it needs no tiers, no prices and no config beyond the texts. */
  var PRODUCT_MODE = TIERS.length > 0 && hasDiscount && hasVariants;
  var MAX_QTY = int(cfg.maxQuantity, 20);
  if (!(MAX_QTY >= 1)) MAX_QTY = 20;

  var SHOW = {
    table: cfg.showTable !== false,
    savings: cfg.showSavings !== false,
    progress: cfg.showProgress !== false
  };

  var PRODUCT_ID = cfg.productId === undefined ? null : cfg.productId;
  var PRODUCT_HANDLE = str(cfg.productHandle);

  /* ---------------- money ----------------
     Shopify's own format string, cleaned of the markup some shops carry in
     money_format (<span class=money>…</span>) because it is written with
     textContent, never innerHTML. There is no fallback currency symbol: if
     the format string cannot be understood the bare number is shown rather
     than a wrong currency. */

  var MONEY_PLACEHOLDER = /\{\{\s*(\w+)\s*\}\}/;
  var MONEY_FORMAT = (function () {
    var f = typeof cfg.moneyFormat === 'string' ? cfg.moneyFormat : '';
    f = f.replace(/<[^>]*>/g, '');
    f = f.replace(/&amp;/g, '&').replace(/&#39;|&apos;/g, "'").replace(/&quot;/g, '"');
    return f.replace(/^\s+|\s+$/g, '');
  })();

  function groupNumber(cents, precision, thousands, decimal) {
    var value = num(cents, 0) / 100;
    var fixed;
    try { fixed = value.toFixed(precision); } catch (e) { fixed = String(value); }
    var parts = fixed.split('.');
    var whole = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
    var frac = parts[1] ? decimal + parts[1] : '';
    return whole + frac;
  }

  function money(cents) {
    var amount = num(cents, 0);
    if (!isFinite(amount)) amount = 0;
    var match = MONEY_FORMAT ? MONEY_FORMAT.match(MONEY_PLACEHOLDER) : null;
    if (!match) return groupNumber(amount, 2, ',', '.');

    var value;
    switch (match[1]) {
      case 'amount_no_decimals':
        value = groupNumber(amount, 0, ',', '.'); break;
      case 'amount_with_comma_separator':
        value = groupNumber(amount, 2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator':
        value = groupNumber(amount, 0, '.', ','); break;
      case 'amount_with_apostrophe_separator':
        value = groupNumber(amount, 2, "'", '.'); break;
      case 'amount_with_space_separator':
        value = groupNumber(amount, 2, ' ', ','); break;
      case 'amount_no_decimals_with_space_separator':
        value = groupNumber(amount, 0, ' ', ','); break;
      case 'amount_with_period_and_space_separator':
        value = groupNumber(amount, 2, ' ', '.'); break;
      default:
        value = groupNumber(amount, 2, ',', '.'); break;
    }
    return MONEY_FORMAT.replace(MONEY_PLACEHOLDER, value);
  }

  /* ---------------- the page: quantity ----------------
     Candidates are gathered document-wide because there is no reliable way
     to tell which of the eight buy boxes owns this component. They are then
     scored, and the shopper's last touched input always wins. */

  var QTY_SELECTOR = [
    'gp-product-quantity input[name="quantity"]',
    'input[name="quantity"]',
    'select[name="quantity"]',
    'input.quantity__input',
    '[data-quantity-input]'
  ].join(',');

  var QTY_CONTAINER = [
    'gp-product-quantity',
    '.obk-qty',
    'quantity-selector',
    'quantity-input',
    '.quantity',
    '.quantity-selector',
    '.quantity__input-wrapper',
    '[data-quantity]',
    '[class*="quantity"]'
  ].join(',');

  var candidates = [];
  var touched = null;   /* the input the shopper last interacted with */
  var source = null;    /* the input currently believed to be the live one */

  function isQtyNode(el) {
    if (!el || el.nodeType !== 1) return false;
    var name = '';
    try { name = (el.getAttribute && el.getAttribute('name')) || ''; } catch (e) { name = ''; }
    if (name === 'quantity') return true;
    try { if (el.matches && el.matches(QTY_SELECTOR)) return true; } catch (e) { /* ignore */ }
    return false;
  }

  function usableQty(el) {
    if (!el || !el.parentNode) return false;
    if (isClone(el)) return false;
    if (closestEl(el, 'cart-items-component, [data-cart-item], [data-ob-vd-cart], product-card, product-recommendations, #ob-quantity-popup')) return false;
    var owner = closestEl(el, '[data-obk-product], [data-product-id], gp-product');
    if (owner) {
      var ownerId = owner.getAttribute('data-obk-product') || owner.getAttribute('data-product-id');
      if (!ownerId && owner.tagName.toLowerCase() === 'gp-product') {
        try { ownerId = JSON.parse(owner.getAttribute('gp-context') || '{}').productId; } catch (e) {}
      }
      if (ownerId && String(ownerId) !== String(PRODUCT_ID)) return false;
    }
    /* a cart line, not a buy box */
    var form = closestEl(el, 'form');
    if (form) {
      var action = '';
      try { action = form.getAttribute('action') || ''; } catch (e) { action = ''; }
      if (action.indexOf('/cart/change') > -1 || action.indexOf('/cart/update') > -1) return false;
    }
    return true;
  }

  function collectCandidates() {
    var found = qsa(QTY_SELECTOR);
    var out = [];
    for (var i = 0; i < found.length; i++) {
      if (usableQty(found[i]) && isRendered(found[i])) out.push(found[i]);
    }
    return out;
  }

  function scoreQty(el) {
    var s = 0;
    if (isRendered(el)) s += 100;
    var form = closestEl(el, 'form');
    if (form) {
      var action = '';
      try { action = form.getAttribute('action') || ''; } catch (e) { action = ''; }
      if (action.indexOf('/cart/add') > -1) s += 20;
      if (isRendered(form)) s += 40;
    }
    if (closestEl(el, 'gp-product-quantity')) s += 10;
    if (el.hasAttribute('data-obk-qty')) s += 30;
    /* a sticky bar is a real, visible buy box, but it is the least likely one
       the shopper is looking at — only ever used to break a tie */
    if (closestEl(el, '[class*="sticky"], [class*="Sticky"], [data-sticky]')) s -= 30;
    if (readRawQty(el) === null) s -= 15;
    return s;
  }

  function resolveSource() {
    candidates = collectCandidates();

    /* the last touched input wins, but only while it is still rendered — a
       rotation or a breakpoint change can retire the box it lived in */
    if (touched && touched.parentNode && usableQty(touched) && isRendered(touched)) return touched;
    touched = null;

    var best = null;
    var bestScore = -1e9;
    for (var i = 0; i < candidates.length; i++) {
      var s = scoreQty(candidates[i]);
      if (s > bestScore) { bestScore = s; best = candidates[i]; }
    }
    return best;
  }

  function readRawQty(el) {
    if (!el) return null;
    var raw = '';
    try {
      raw = el.value;
      if (raw === '' || raw === undefined || raw === null) {
        raw = (el.getAttribute && el.getAttribute('value')) || '';
      }
    } catch (e) { return null; }
    if (raw === '' || raw === null || raw === undefined) return null;
    var n = int(raw, NaN);
    if (!isFinite(n)) return null;
    return n;
  }

  /* quantity 0, a blank input, a negative or an absurd value are all read as
     "no line yet": the figures hide and the progress line points at the
     first real tier. A quantity above maxQuantity is NOT clamped — the
     Shopify automatic discount has no ceiling, so neither may the preview. */
  function readQty() {
    source = resolveSource();
    var n = readRawQty(source);
    if (n === null) return source ? 0 : 1;
    if (!(n > 0)) return 0;
    if (n > 1000000) return 1000000;
    return n;
  }

  /* ---------------- the page: variant ---------------- */

  function variantKey(value) {
    if (value === null || value === undefined) return '';
    var key = String(value).replace(/[^0-9]/g, '');
    if (!key) return '';
    return Object.prototype.hasOwnProperty.call(VARIANTS, key) ? key : '';
  }

  var variantId = variantKey(cfg.selectedVariantId) || firstVariantKey();

  function firstVariantKey() {
    for (var key in VARIANTS) {
      if (Object.prototype.hasOwnProperty.call(VARIANTS, key)) return key;
    }
    return '';
  }

  function readVariantFromDom() {
    var inputs = qsa('input[name="id"], select[name="id"]');
    var best = '';
    var bestScore = -1e9;
    for (var i = 0; i < inputs.length; i++) {
      var el = inputs[i];
      if (isClone(el) || closestEl(el, 'cart-items-component, product-card, product-recommendations')) continue;
      if (!isRendered(el)) continue;
      var value = '';
      try { value = String(el.value || (el.getAttribute && el.getAttribute('value')) || ''); } catch (e) { value = ''; }
      value = value.replace(/[^0-9]/g, '');
      if (!value) continue;
      if (!Object.prototype.hasOwnProperty.call(VARIANTS, value)) continue;
      var s = 0;
      var form = closestEl(el, 'form');
      if (form && isRendered(form)) s += 40;
      if (source && form && form === closestEl(source, 'form')) s += 60;
      if (s > bestScore) { bestScore = s; best = value; }
    }
    return best;
  }

  function variantIdFromEvent(e) {
    var d = e && e.detail;
    if (!d) return '';
    var candidatesIds = [];
    try {
      if (d.data && d.data.variant && d.data.variant.id) candidatesIds.push(d.data.variant.id);
      if (d.variant && d.variant.id) candidatesIds.push(d.variant.id);
      if (d.resource && d.resource.id) candidatesIds.push(d.resource.id);
      if (d.id) candidatesIds.push(d.id);
    } catch (e2) { return ''; }
    for (var i = 0; i < candidatesIds.length; i++) {
      var value = String(candidatesIds[i]).replace(/[^0-9]/g, '');
      if (value && Object.prototype.hasOwnProperty.call(VARIANTS, value)) return value;
    }
    return '';
  }

  /* the price is looked up on every single render; nothing about the previous
     variant is ever carried forward */
  function variantInfo(id) {
    var entry = Object.prototype.hasOwnProperty.call(VARIANTS, id) ? VARIANTS[id] : null;
    if (!entry || typeof entry !== 'object') return { price: null, compareAt: null, available: false, known: false };
    var price = num(entry.price, NaN);
    return {
      price: isFinite(price) && price > 0 ? Math.round(price) : null,
      compareAt: isFinite(num(entry.compareAt, NaN)) ? Math.round(num(entry.compareAt, 0)) : null,
      available: entry.available !== false,
      known: true
    };
  }

  /* ---------------- the maths ----------------
     Applied to the LINE, then divided back out, because that is how Shopify
     applies a percentage automatic discount: it discounts the line total and
     apportions it across the units. The line total is therefore the exact
     figure and the per-unit figure is the derived one.

     compareAt is carried in the config but is deliberately absent from every
     calculation below. The saving is always measured against the CURRENT
     price; measuring it against a compare-at price would double-count the
     markdown and the box would be lying. */

  function discountForTier(tier, unitPrice, qty) {
    if (!tier || !(unitPrice > 0) || !(qty > 0)) return 0;
    var lineBefore = unitPrice * qty;
    var off = 0;
    if (tier.discountType === 'amount') {
      var perUnit = Math.round(tier.discountValue * 100);
      if (!(perUnit > 0)) return 0;
      off = perUnit * qty;
    } else {
      if (!(tier.discountValue > 0)) return 0;
      off = Math.round(lineBefore * tier.discountValue / 100);
    }
    if (off < 0) off = 0;
    if (off > lineBefore) off = lineBefore;
    return off;
  }

  /* Shopify picks the tier that gives the shopper the better price, not
     merely the highest qualifying minimum — proven live on this store, where
     a cart of 3 took the better of two overlapping tiers. Mirror that. */
  function activeTierFor(unitPrice, qty) {
    var best = null;
    var bestOff = -1;
    for (var i = 0; i < TIERS.length; i++) {
      var tier = TIERS[i];
      if (qty < tier.minQuantity) continue;
      var off = discountForTier(tier, unitPrice, qty);
      if (off > bestOff || (off === bestOff && best && tier.minQuantity > best.minQuantity)) {
        bestOff = off;
        best = tier;
      }
    }
    return best;
  }

  /* the next tier is the nearest one above the current quantity that would
     actually beat the current unit price — never one that merely has a
     higher minimum */
  function nextTierFor(unitPrice, qty, currentUnitAfter) {
    var wanted = null;
    for (var i = 0; i < TIERS.length; i++) {
      var tier = TIERS[i];
      if (tier.minQuantity <= qty) continue;
      var atTier = unitAfterAt(tier, unitPrice, tier.minQuantity);
      if (atTier === null) continue;
      if (currentUnitAfter !== null && atTier >= currentUnitAfter) continue;
      if (!wanted || tier.minQuantity < wanted.minQuantity) wanted = tier;
    }
    return wanted;
  }

  function unitAfterAt(tier, unitPrice, qty) {
    if (!(unitPrice > 0) || !(qty > 0)) return null;
    var off = discountForTier(tier, unitPrice, qty);
    return (unitPrice * qty - off) / qty;
  }

  var state = {
    version: VERSION,
    productId: PRODUCT_ID,
    productHandle: PRODUCT_HANDLE,
    variantId: '',
    variantKnown: false,
    available: false,
    quantity: 0,
    unitBefore: null,
    unitAfter: null,
    lineBefore: null,
    lineAfter: null,
    saved: null,
    tier: null,
    nextTier: null,
    needed: 0,
    atBest: false,
    valid: false
  };

  function compute() {
    var qty = readQty();
    var id = variantId;
    var info = variantInfo(id);

    state.variantId = id;
    state.variantKnown = info.known;
    state.available = info.available;
    state.quantity = qty;
    state.unitBefore = info.price;
    state.tier = null;
    state.nextTier = null;
    state.needed = 0;
    state.atBest = false;
    state.unitAfter = null;
    state.lineBefore = null;
    state.lineAfter = null;
    state.saved = null;

    /* an unknown variant, a variant with no price, or a sold-out variant:
       nothing honest can be previewed, so nothing is shown. Hiding on a
       sold-out variant is the same behaviour as the delivery-estimate
       widget, so the product page stays consistent. */
    state.valid = !!(info.known && info.price !== null && info.available);
    if (!state.valid) return state;

    var unit = info.price;
    var tier = qty > 0 ? activeTierFor(unit, qty) : null;
    state.tier = tier;

    if (qty > 0) {
      state.lineBefore = unit * qty;
      var off = discountForTier(tier, unit, qty);
      state.lineAfter = state.lineBefore - off;
      state.saved = off;
      state.unitAfter = state.lineAfter / qty;
    }

    var reference = qty > 0 ? state.unitAfter : unit;
    var next = nextTierFor(unit, qty, reference);
    if (next) {
      state.nextTier = next;
      state.needed = next.minQuantity - (qty > 0 ? qty : 0);
      if (state.needed < 1) state.needed = 1;
    } else {
      /* only "our best price" once a tier is actually active */
      state.atBest = !!(tier && discountForTier(tier, unit, qty) > 0);
    }
    return state;
  }

  /* ---------------- rendering ---------------- */

  var instances = [];
  var activeInstance = null;

  /* Placement belongs to the main buy box, even when a sticky quantity input
     becomes the current source for the price calculation. */
  function isStickyPurchase(el) {
    return !!closestEl(el, '.sticky-add-to-cart, sticky-add-to-cart, [data-sticky]');
  }

  function placementAnchor() {
    var inputs = collectCandidates().filter(function (el) { return !isStickyPurchase(el); });
    inputs.sort(function (a, b) { return scoreQty(b) - scoreQty(a); });
    var input = inputs[0];
    if (!input) return null;
    var nativeBox = closestEl(input, '[data-obk]');
    if (nativeBox) return nativeBox;
    var gemQuantity = closestEl(input, 'gp-product-quantity');
    if (gemQuantity) {
      /* Find the branch below the shared price/quantity container. A quantity
         control may sit inside several narrow, nested GemPages rows. */
      var branch = gemQuantity;
      var owner = closestEl(input, 'gp-product');
      while (branch.parentElement && branch.parentElement !== owner) {
        if (qs('gp-product-price', branch.parentElement)) return branch;
        branch = branch.parentElement;
      }
      return closestEl(gemQuantity, 'gp-row') || gemQuantity;
    }
    return closestEl(input, 'quantity-selector, quantity-input, .quantity-selector');
  }

  function ensurePlacement() {
    var existing = qsa('[data-ob-vd]').filter(function (el) {
      return !isClone(el) && !isStickyPurchase(el) && !el.hasAttribute('data-ob-vd-auto') && isRendered(el.parentElement);
    });
    if (existing.length) return;
    var anchor = placementAnchor();
    if (!anchor) {
      anchor = qsa('gp-product-price, product-price').filter(function (el) {
        return !isClone(el) && !isStickyPurchase(el) && isRendered(el) && !closestEl(el, 'product-card, product-recommendations');
      })[0];
    }
    if (!anchor) return;
    var auto = qs('[data-ob-vd-auto]');
    if (!auto) {
      var template = qs('template[data-ob-vd-template]');
      if (!template) return;
      var fragment = template.content.cloneNode(true);
      auto = qs('[data-ob-vd]', fragment);
      if (!auto) return;
      auto.setAttribute('data-ob-vd-auto', '');
      anchor.parentNode.insertBefore(fragment, anchor.nextSibling);
    } else if (anchor.nextElementSibling !== auto) {
      anchor.parentNode.insertBefore(auto, anchor.nextSibling);
    }
  }

  function selectInstance() {
    activeInstance = null;
    for (var i = 0; i < instances.length; i++) {
      var inst = instances[i];
      if (isClone(inst.el) || isStickyPurchase(inst.el) || !isRendered(inst.el.parentElement)) continue;
      if (!activeInstance || (activeInstance.el.hasAttribute('data-ob-vd-auto') && !inst.el.hasAttribute('data-ob-vd-auto'))) activeInstance = inst;
    }
  }


  function hydrate(root) {
    var nodes = qsa('[data-ob-vd]', root || document);
    for (var i = 0; i < nodes.length; i++) {
      var el = nodes[i];
      if (el.getAttribute('data-ob-vd-ready') === '1') continue;
      if (isClone(el)) continue;
      el.setAttribute('data-ob-vd-ready', '1');

      var inst = {
        el: el,
        context: el.getAttribute('data-ob-vd-context') || '',
        title: qs('[data-ob-vd-title]', el),
        caption: qs('[data-ob-vd-caption]', el),
        colQty: qs('[data-ob-vd-col-qty]', el),
        colUnit: qs('[data-ob-vd-col-unit]', el),
        colSave: qs('[data-ob-vd-col-save]', el),
        table: qs('[data-ob-vd-table]', el),
        rows: qs('[data-ob-vd-rows]', el),
        figures: qs('[data-ob-vd-figures]', el),
        baseValue: qs('[data-ob-vd-base-value]', el),
        unitLabel: qs('[data-ob-vd-unit-label]', el),
        unitValue: qs('[data-ob-vd-unit-value]', el),
        unitWas: qs('[data-ob-vd-unit-was]', el),
        totalLine: qs('[data-ob-vd-total-line]', el),
        totalLabel: qs('[data-ob-vd-total-label]', el),
        totalValue: qs('[data-ob-vd-total-value]', el),
        savedLine: qs('[data-ob-vd-saved-line]', el),
        savedLabel: qs('[data-ob-vd-saved-label]', el),
        savedValue: qs('[data-ob-vd-saved-value]', el),
        progress: qs('[data-ob-vd-progress]', el),
        rowSig: '',
        sig: '',
        rowNodes: []
      };
      applyStatic(inst);
      instances.push(inst);
    }
  }

  /* the config is the single authority for every string: the snippet
     server-renders the same values so there is no flash, and these writes
     make the config win if the two ever differ */
  function applyStatic(inst) {
    if (T.heading) setText(inst.title, T.heading);
    if (T.caption) setText(inst.caption, T.caption);
    if (T.colQuantity) setText(inst.colQty, T.colQuantity);
    if (T.colUnit) setText(inst.colUnit, T.colUnit);
    if (T.colSave) setText(inst.colSave, T.colSave);
    if (T.unitLabel) setText(inst.unitLabel, T.unitLabel);
    if (T.savedLabel) setText(inst.savedLabel, T.savedLabel);
    show(inst.table, SHOW.table);
    show(inst.colSave, SHOW.savings);
  }

  function prune() {
    var kept = [];
    for (var i = 0; i < instances.length; i++) {
      var el = instances[i].el;
      if (el && el.parentNode && document.documentElement.contains(el)) kept.push(instances[i]);
    }
    instances = kept;
  }

  function cell(tag, className) {
    var node = document.createElement(tag);
    node.className = className;
    return node;
  }

  /**
   * A tier's discount as words: "10% OFF", or "Rs. 50.00 OFF" for a per-unit amount.
   *
   * The same phrasing the cart prompt builds in Liquid, so one offer reads the same on
   * the product page and in the cart. It describes a tier; it is never a claim that a
   * discount has been applied, which only the cart can say and only from Shopify's own
   * allocation.
   */
  function tierDiscountLabel(tier) {
    if (!tier) return '';
    var value = tier.discountValue;
    if (!(value > 0)) return '';
    if (tier.discountType === 'amount') return money(Math.round(value * 100)) + ' OFF';
    return value + '% OFF';
  }

  /* Rows are built with createElement + textContent, never innerHTML: tier
     labels and badges can come from a product metafield, which is merchant
     input and is treated as text, never as markup. */
  function buildRows(inst) {
    if (!inst.rows) return;
    while (inst.rows.firstChild) inst.rows.removeChild(inst.rows.firstChild);
    inst.rowNodes = [];

    var info = variantInfo(state.variantId);
    var unit = info.price;

    for (var i = 0; i < TIERS.length; i++) {
      var tier = TIERS[i];
      if (tier.minQuantity > MAX_QTY) continue;

      var tr = document.createElement('tr');
      tr.className = 'ob-vd__row';
      tr.setAttribute('data-ob-vd-row', '');
      tr.setAttribute('data-ob-vd-min', String(tier.minQuantity));

      var th = cell('th', 'ob-vd__cell ob-vd__cell--label');
      th.setAttribute('scope', 'row');

      var label = cell('span', 'ob-vd__qty');
      label.textContent = tier.label;
      th.appendChild(label);

      if (tier.badge) {
        var badge = cell('span', 'ob-vd__badge');
        badge.textContent = tier.badge;
        th.appendChild(badge);
      }

      var marker = cell('span', 'ob-vd__marker');
      marker.setAttribute('data-ob-vd-marker', '');
      marker.textContent = T.selectedLabel || '';
      marker.hidden = true;
      th.appendChild(marker);
      tr.appendChild(th);

      var unitCell = cell('td', 'ob-vd__cell ob-vd__cell--num');
      var atTier = unitAfterAt(tier, unit, tier.minQuantity);
      unitCell.textContent = atTier === null ? (T.noneLabel || '') : money(Math.round(atTier));
      tr.appendChild(unitCell);

      if (SHOW.savings) {
        var saveCell = cell('td', 'ob-vd__cell ob-vd__cell--num');
        var off = unit === null ? 0 : discountForTier(tier, unit, tier.minQuantity);
        saveCell.textContent = off > 0 ? money(off) : (T.noneLabel || '');
        tr.appendChild(saveCell);
      }

      inst.rows.appendChild(tr);
      inst.rowNodes.push({ tr: tr, marker: marker, min: tier.minQuantity });
    }
  }

  function markRows(inst) {
    var activeMin = state.tier ? state.tier.minQuantity : -1;
    for (var i = 0; i < inst.rowNodes.length; i++) {
      var row = inst.rowNodes[i];
      var isActive = row.min === activeMin;
      var className = isActive ? 'ob-vd__row ob-vd__row--active' : 'ob-vd__row';
      if (row.tr.className !== className) row.tr.className = className;
      if (isActive) row.tr.setAttribute('aria-current', 'true');
      else row.tr.removeAttribute('aria-current');
      show(row.marker, isActive && !!(T.selectedLabel));
    }
  }

  function renderInstance(inst) {
    show(inst.el, state.valid && inst === activeInstance);
    if (inst !== activeInstance) return;
    if (!state.valid) return;

    var rowSig = [state.variantId, String(state.unitBefore), String(TIERS.length), String(SHOW.savings)].join('|');
    if (inst.rowSig !== rowSig) {
      buildRows(inst);
      inst.rowSig = rowSig;
      inst.sig = '';
    }

    var sig = [
      state.variantId,
      state.quantity,
      state.tier ? state.tier.minQuantity : 'none',
      state.lineAfter,
      state.saved,
      state.nextTier ? state.nextTier.minQuantity : 'none',
      state.needed,
      state.atBest
    ].join('|');
    if (inst.sig === sig) return;
    inst.sig = sig;

    markRows(inst);

    var hasLine = state.quantity > 0 && state.lineAfter !== null;
    show(inst.figures, hasLine);

    if (hasLine) {
      setText(inst.baseValue, money(state.unitBefore));
      setText(inst.unitValue, money(Math.round(state.unitAfter)));
      var discounted = state.saved > 0;
      /* the struck-through figure is the CURRENT unit price, never a
         compare-at price */
      if (inst.unitWas) {
        setText(inst.unitWas, discounted ? money(state.unitBefore) : '');
        show(inst.unitWas, discounted);
      }
      setText(inst.totalLabel, fill(T.totalLabel, { n: state.quantity }));
      setText(inst.totalValue, money(state.lineAfter));
      var showSaved = SHOW.savings;
      show(inst.savedLine, showSaved);
      if (showSaved) setText(inst.savedValue, money(state.saved));
    }

    if (inst.progress) {
      var line = '';
      if (SHOW.progress) {
        if (state.nextTier && state.needed > 0) {
          line = fill(T.progressText, {
            n: state.needed,
            label: state.nextTier.label,
            discount: tierDiscountLabel(state.nextTier)
          });
        } else if (state.atBest) {
          /* {discount} here is the tier the shopper has ALREADY reached, which is why it
             is filled from state.tier and not from a next tier that does not exist */
          line = fill(T.bestText, { discount: tierDiscountLabel(state.tier) });
        }
      }
      setText(inst.progress, line);
      show(inst.progress, !!line);
    }
  }

  /* ---------------- analytics ---------------- */

  var viewed = false;
  var lastTierMin = null;

  function track() {
    if (!state.valid) return;

    if (!viewed && instances.length) {
      viewed = true;
      dataLayerPush({
        event: 'ob_vd_viewed',
        ob_vd_product_id: PRODUCT_ID,
        ob_vd_product_handle: PRODUCT_HANDLE,
        ob_vd_variant_id: state.variantId,
        ob_vd_quantity: state.quantity,
        ob_vd_tiers: TIERS.length
      });
    }

    var min = state.tier && state.saved > 0 ? state.tier.minQuantity : 0;
    if (lastTierMin === null) { lastTierMin = min; return; }
    if (min === lastTierMin) return;

    var event = min > lastTierMin ? 'ob_vd_tier_reached' : 'ob_vd_tier_lost';
    lastTierMin = min;
    dataLayerPush({
      event: event,
      ob_vd_tier: state.tier ? state.tier.label : '',
      ob_vd_tier_min_quantity: min,
      ob_vd_quantity: state.quantity,
      ob_vd_variant_id: state.variantId,
      ob_vd_product_id: PRODUCT_ID
    });
  }

  /* ---------------- the loop ---------------- */

  var rendering = false;
  var readTimer = null;
  var settleTimer = null;

  function run() {
    if (!PRODUCT_MODE) return;
    if (rendering) return;
    rendering = true;
    try {
      ensurePlacement();
      hydrate(document);
      prune();
      selectInstance();
      compute();
      for (var i = 0; i < instances.length; i++) {
        try { renderInstance(instances[i]); } catch (e) { /* one bad instance must not stop the others */ }
      }
      track();
    } catch (e) {
      /* a broken page must never take the product page down with it */
    }
    rendering = false;
  }

  /* two reads: one now, one after the plus/minus control has written the
     value it writes asynchronously */
  function scheduleRead() {
    if (!PRODUCT_MODE) return;
    if (readTimer === null) readTimer = setTimeout(function () { readTimer = null; run(); }, READ_DELAY);
    if (settleTimer !== null) clearTimeout(settleTimer);
    settleTimer = setTimeout(function () { settleTimer = null; run(); }, READ_SETTLE);
  }

  function onQtyEvent(e) {
    var target = e && e.target;
    if (!target) return;
    if (e.type === 'quantity-selector:update') target = qs(QTY_SELECTOR, target) || target;
    if (isQtyNode(target) && usableQty(target)) {
      touched = target;
      observeCandidate(target);
      scheduleRead();
      return;
    }
    /* a variant picker: a radio, a select or a hidden id input */
    var name = '';
    try { name = (target.getAttribute && target.getAttribute('name')) || ''; } catch (e2) { name = ''; }
    if (name === 'id' || closestEl(target, 'variant-picker, [data-variant-picker], .variant-picker, gp-product-variants')) {
      variantId = readVariantFromDom() || variantId;
      scheduleRead();
    }
  }

  /* Any click, anywhere: the plus/minus controls in eight different buy boxes
     cannot be enumerated reliably, and a click that changes nothing costs one
     signature comparison and no DOM write at all. When the click landed on or
     inside a quantity control, that control's input also becomes the source.

     This is also the single delegated listener the cart prompts use. The cart
     drawer replaces its whole DOM on every change, so a listener bound to a
     button would die with it; one listener on document survives everything. */
  function onClick(e) {
    var target = e && e.target;
    if (target && target.nodeType === 1) {
      var jump = closestEl(target, '[data-ob-vd-jump]');
      if (jump) {
        if (e.preventDefault) e.preventDefault();
        jumpToQuantity(jump);
        return;
      }
      var container = closestEl(target, QTY_CONTAINER);
      if (container) {
        var input = qs('input[name="quantity"], select[name="quantity"], input.quantity__input', container);
        if (input && usableQty(input)) {
          touched = input;
          observeCandidate(input);
        }
      }
    }
    scheduleRead();
  }

  function onVariantEvent(e) {
    var fromEvent = variantIdFromEvent(e);
    if (fromEvent) variantId = fromEvent;
    else if (e && e.detail && (e.detail.resource === null || (e.detail.data && String(e.detail.data.productId) === String(PRODUCT_ID)))) variantId = '';
    else return;
    scheduleRead();
  }

  /* ---------------- observers ---------------- */

  var attrObserver = null;
  var domObserver = null;
  var domTimer = null;
  var observed = [];

  function alreadyObserved(el) {
    for (var i = 0; i < observed.length; i++) if (observed[i] === el) return true;
    return false;
  }

  /* GemPages writes the hidden quantity input's value attribute
     programmatically and fires nothing, so the attribute itself is watched */
  function observeCandidate(el) {
    if (!attrObserver || !el || alreadyObserved(el)) return;
    try {
      attrObserver.observe(el, { attributes: true, attributeFilter: ['value'] });
      observed.push(el);
    } catch (e) { /* ignore */ }
  }

  function observeAll() {
    candidates = collectCandidates();
    for (var i = 0; i < candidates.length; i++) observeCandidate(candidates[i]);
    var ids = qsa('input[name="id"], select[name="id"]');
    for (var j = 0; j < ids.length; j++) if (!isClone(ids[j])) observeCandidate(ids[j]);
  }

  function startObservers() {
    if (typeof window.MutationObserver !== 'function') return;

    attrObserver = new window.MutationObserver(function (records) {
      for (var i = 0; i < records.length; i++) {
        var target = records[i].target;
        if (!target) continue;
        var name = '';
        try { name = (target.getAttribute && target.getAttribute('name')) || ''; } catch (e) { name = ''; }
        if (name === 'id') {
          variantId = readVariantFromDom() || variantId;
        } else if (isRendered(target) && usableQty(target)) {
          /* a programmatic write to a rendered input is as good a signal as
             a click on it */
          touched = target;
        }
      }
      scheduleRead();
    });

    domObserver = new window.MutationObserver(function (records) {
      var relevant = false;
      for (var i = 0; i < records.length; i++) {
        var target = records[i].target;
        /* our own writes must not feed themselves back in */
        if (target && target.nodeType === 1 && closestEl(target, '[data-ob-vd]')) continue;
        relevant = true;
        break;
      }
      if (!relevant) return;
      if (domTimer !== null) clearTimeout(domTimer);
      domTimer = setTimeout(function () {
        domTimer = null;
        hydrate(document);
        hydrateCart(document);
        observeAll();
        /* a new buy box may have appeared or the old one may have gone */
        if (touched && (!usableQty(touched) || !isRendered(touched))) touched = null;
        variantId = readVariantFromDom() || variantId;
        run();
      }, DOM_DEBOUNCE);
    });

    try {
      domObserver.observe(document.body || document.documentElement, { childList: true, subtree: true });
    } catch (e) { /* ignore */ }
    observeAll();
  }

  /* ==========================================================================
     CART MODE

     The cart is where the discount is genuinely applied, so nothing here
     computes a price. snippets/oblivion-vd-cart-item.liquid server-renders
     each line's earned state and its remaining tiers, and every cart change
     re-renders that markup through the theme's own section rendering. This
     code only: handles the tap, drives the theme's own quantity path, keeps
     one change in flight at a time, and reports failures.

     It changes QUANTITY and nothing else. The only input it ever writes is
     the cart line's own name="updates[]" stepper — never a name="quantity"
     buy-box input, never a price field, never a line property.
     ========================================================================== */

  /* ThemeEvents.quantitySelectorUpdate / cartUpdate / cartError, from
     assets/events.js. cart-items-component listens for the first on document,
     debounced 300ms, reads { quantity, cartLine } off the detail, POSTs the
     change itself and morphs the affected sections. Dispatching it is
     therefore the theme's own path, not a reimplementation of it. */
  var CART_QTY_EVENT = 'quantity-selector:update';
  var CART_UPDATE_EVENT = 'cart:update';
  var CART_ERROR_EVENT = 'cart:error';
  var CART_CHANGE_PATH = 'cart/change.js';
  /* the safety net for a request that never resolves. It can be generous
     because the operation is idempotent by construction: a button sends an
     ABSOLUTE quantity, never an increment, so the same tap twice asks for the
     same cart, not for twice as much. */
  var CART_BUSY_TIMEOUT = 6000;

  var cartBusy = false;
  var cartBusyTimer = null;
  var cartSeen = {};

  function cartRootOf(el) { return closestEl(el, '[data-ob-vd-cart]'); }

  function cartButtonsIn(root) { return qsa('[data-ob-vd-jump]', root || document); }

  /* every prompt on the page is disabled while a change is in flight: one
     cart change at a time, so a double tap cannot add twice, and the safety
     timeout means a request that never resolves cannot leave the cart frozen */
  function setCartBusy(busy) {
    cartBusy = !!busy;
    var btns = cartButtonsIn(document);
    var i;
    for (i = 0; i < btns.length; i++) {
      try { btns[i].disabled = !!busy; } catch (e) { /* ignore */ }
    }
    var roots = qsa('[data-ob-vd-cart]');
    for (i = 0; i < roots.length; i++) {
      try { roots[i].setAttribute('aria-busy', busy ? 'true' : 'false'); } catch (e) { /* ignore */ }
    }
    if (cartBusyTimer !== null) { clearTimeout(cartBusyTimer); cartBusyTimer = null; }
    if (busy) {
      cartBusyTimer = setTimeout(function () { cartBusyTimer = null; setCartBusy(false); }, CART_BUSY_TIMEOUT);
    }
  }

  function showCartError(root, message) {
    var nodes = root ? qsa('[data-ob-vd-cart-error]', root) : qsa('[data-ob-vd-cart-error]');
    for (var i = 0; i < nodes.length; i++) {
      setText(nodes[i], message || '');
      show(nodes[i], !!message);
    }
  }

  function clearCartErrors() { showCartError(null, ''); }

  /* the line's own stepper, preferring the one in the same cart component as
     the prompt — the cart page and the drawer can both be in the DOM */
  /**
   * The shop root, which is NOT always "/". A market with a locale or country prefix
   * serves the cart API under it — /en-in/cart/change.js — and a request to the bare
   * path there is a redirect at best and a 404 at worst. Shopify publishes the prefix
   * as window.Shopify.routes.root, always with a trailing slash; this reads it at call
   * time rather than at load, because the theme may define it after this script.
   */
  function shopRoot() {
    var root = '/';
    try {
      var published = window.Shopify && window.Shopify.routes && window.Shopify.routes.root;
      if (typeof published === 'string' && published) root = published;
    } catch (e) { /* keep "/" */ }
    if (root.charAt(root.length - 1) !== '/') root += '/';
    return root;
  }

  function cartChangeUrl() {
    try {
      if (window.Theme && window.Theme.routes && window.Theme.routes.cart_change_url) {
        return window.Theme.routes.cart_change_url;
      }
    } catch (e) { /* fall through to the published root */ }
    return shopRoot() + CART_CHANGE_PATH;
  }

  function lineQuantityInput(line, root) {
    var sel = 'input[name="updates[]"][data-cart-line="' + line + '"]';
    var scope = root ? closestEl(root, 'cart-items-component') : null;
    var found = scope ? qs(sel, scope) : null;
    if (found) return found;
    var all = qsa(sel);
    for (var i = 0; i < all.length; i++) {
      if (isClone(all[i])) continue;
      if (isRendered(all[i])) return all[i];
    }
    for (var j = 0; j < all.length; j++) {
      if (!isClone(all[j])) return all[j];
    }
    return null;
  }

  function makeEvent(name, detail) {
    try {
      return new window.CustomEvent(name, { bubbles: true, detail: detail });
    } catch (e) { /* fall through */ }
    try {
      var ev = document.createEvent('CustomEvent');
      ev.initCustomEvent(name, true, false, detail);
      return ev;
    } catch (e2) { return null; }
  }

  function post(url, body, done) {
    var finished = false;
    function finish(ok, data) {
      if (finished) return;
      finished = true;
      try { done(ok, data); } catch (e) { /* never throw out of a callback */ }
    }

    if (typeof window.fetch === 'function') {
      try {
        window.fetch(url, {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: body
        }).then(function (res) {
          var ok = !!(res && res.ok);
          res.text().then(function (textBody) {
            var parsed = null;
            try { parsed = JSON.parse(textBody); } catch (e) { parsed = null; }
            if (parsed && parsed.errors) ok = false;
            finish(ok, parsed);
          })['catch'](function () { finish(ok, null); });
        })['catch'](function () { finish(false, null); });
        return;
      } catch (e) { /* fall through to XHR */ }
    }

    try {
      var xhr = new XMLHttpRequest();
      xhr.open('POST', url, true);
      xhr.setRequestHeader('Content-Type', 'application/json');
      xhr.setRequestHeader('Accept', 'application/json');
      xhr.onreadystatechange = function () {
        if (xhr.readyState !== 4) return;
        var parsed = null;
        try { parsed = JSON.parse(xhr.responseText); } catch (e) { parsed = null; }
        var ok = xhr.status >= 200 && xhr.status < 300 && !(parsed && parsed.errors);
        finish(ok, parsed);
      };
      xhr.onerror = function () { finish(false, null); };
      xhr.send(body);
    } catch (e) {
      finish(false, null);
    }
  }

  /* the theme's own re-render: every cart-items-component, the cart bubble and
     the drawer listen for cart:update and either morph the sections handed to
     them or re-render themselves through sectionRenderer */
  function dispatchCartUpdate(data) {
    var payload = { source: 'oblivion-vd' };
    if (data && typeof data.item_count === 'number') payload.itemCount = data.item_count;
    if (data && data.sections) payload.sections = data.sections;
    var ev = makeEvent(CART_UPDATE_EVENT, {
      resource: data || null,
      sourceId: 'oblivion-vd',
      data: payload
    });
    if (!ev) return;
    try { document.dispatchEvent(ev); } catch (e) { /* ignore */ }
  }

  /* fallback only — used when the line has no stepper to drive */
  function changeLine(line, quantity, root, key) {
    /* Which sections Shopify should re-render. Two sources, because the drawer and the
       cart page are different sections and a drawer is often NOT a cart-items-component
       with a data-section-id: the id of every such component on the page, plus the
       #shopify-section-* wrapper this particular prompt lives inside. Without the second
       one, tapping a tier inside the drawer re-renders the cart page behind it and
       leaves the drawer showing the old quantity. */
    var sections = [];
    function addSection(id) {
      if (id && sections.indexOf(id) === -1) sections.push(id);
    }

    var comps = qsa('cart-items-component');
    for (var i = 0; i < comps.length; i++) {
      try { addSection(comps[i].getAttribute('data-section-id') || ''); } catch (e) { /* skip */ }
    }

    var wrapper = closestEl(root, '[id^="shopify-section-"]');
    if (wrapper) {
      try { addSection(String(wrapper.id).replace(/^shopify-section-/, '')); } catch (e) { /* skip */ }
    }

    var url = cartChangeUrl();

    /* The line-item KEY is the stable handle: a line number shifts the moment another
       line is removed, and a stale number would re-quantify the wrong product. The key
       is used when the server gave us one; the line number is the fallback.
       /cart/change.js takes either, under `id` or `line`. */
    var payload = {
      quantity: quantity,
      sections: sections.join(','),
      sections_url: window.location.pathname
    };
    if (key) payload.id = key; else payload.line = line;

    var body;
    try {
      body = JSON.stringify(payload);
    } catch (e) { setCartBusy(false); return; }

    post(url, body, function (ok, data) {
      if (!ok) {
        /* the cart is untouched: say so and leave everything as it was */
        setCartBusy(false);
        showCartError(root, T.cartError || '');
        return;
      }
      dispatchCartUpdate(data);
      setCartBusy(false);
    });
  }

  function jumpToQuantity(btn) {
    if (cartBusy) return;

    var root = cartRootOf(btn);
    var line = int(btn.getAttribute('data-ob-vd-line'), 0);
    var key = str(btn.getAttribute('data-ob-vd-key'));
    var quantity = int(btn.getAttribute('data-ob-vd-qty'), 0);
    var label = btn.getAttribute('data-ob-vd-label') || '';

    if (!(line >= 1) || !(quantity >= 1)) return;
    /* the server already refuses to offer a tier this line cannot reach; this
       is the second guard, against a stale or edited attribute */
    if (quantity > MAX_QTY) return;

    clearCartErrors();
    setCartBusy(true);

    dataLayerPush({
      event: 'ob_vd_cart_tier_clicked',
      ob_vd_tier: label,
      ob_vd_quantity: quantity,
      ob_vd_line: line
    });

    var input = lineQuantityInput(line, root);
    if (input) {
      /* exactly what component-quantity-selector.js does: put the value in
         the control, then dispatch the theme's own event from it. The theme
         performs the cart change, the section morph, the bubble update and
         its own error handling — none of that is reimplemented here. */
      try { input.value = String(quantity); } catch (e) { /* readonly is fine */ }
      var ev = makeEvent(CART_QTY_EVENT, { quantity: quantity, cartLine: line });
      if (ev) {
        try {
          input.dispatchEvent(ev);
          return;
        } catch (e) { /* fall through to the direct change */ }
      }
    }

    changeLine(line, quantity, root, key);
  }

  function hydrateCart(root) {
    var nodes = qsa('[data-ob-vd-cart]', root || document);
    for (var i = 0; i < nodes.length; i++) {
      var el = nodes[i];
      if (isClone(el)) continue;
      var count = cartButtonsIn(el).length;
      if (!count) continue; /* an earned-only line is a confirmation, not a prompt */
      var line = el.getAttribute('data-ob-vd-line') || '';
      var qty = el.getAttribute('data-ob-vd-quantity') || '';
      var key = line + ':' + qty + ':' + count;
      if (cartSeen[key]) continue;
      cartSeen[key] = true;
      dataLayerPush({
        event: 'ob_vd_cart_prompt_shown',
        ob_vd_line: int(line, 0),
        ob_vd_quantity: int(qty, 0),
        ob_vd_options: count
      });
    }
  }

  /* ---------------- boot ---------------- */

  function onCartUpdated() {
    setCartBusy(false);
    clearCartErrors();
    hydrateCart(document);
  }

  function onCartFailed() {
    setCartBusy(false);
    showCartError(null, T.cartError || '');
  }

  function boot() {
    try {
      /* ---- cart mode: on every page, because the drawer is on every page.
         One delegated click listener survives the drawer replacing its DOM;
         the theme's own cart events tell us when a change finished. ---- */
      document.addEventListener('click', onClick, true);
      document.addEventListener(CART_UPDATE_EVENT, onCartUpdated);
      document.addEventListener(CART_ERROR_EVENT, onCartFailed);
      document.addEventListener('shopify:section:load', function (e) {
        hydrateCart((e && e.target) || document);
      });
      hydrateCart(document);

      /* ---- product mode: only where there is a priced product to preview ---- */
      if (!PRODUCT_MODE) return;

      hydrate(document);
      variantId = readVariantFromDom() || variantId;
      startObservers();

      document.addEventListener('input', onQtyEvent, true);
      document.addEventListener('change', onQtyEvent, true);
      document.addEventListener('keyup', onQtyEvent, true);
      document.addEventListener('quantity-selector:update', onQtyEvent, true);

      /* Horizon's own event, plus the names other themes and GemPages use */
      document.addEventListener('variant:update', onVariantEvent);
      document.addEventListener('variant:change', onVariantEvent);
      document.addEventListener('variantChange', onVariantEvent);
      document.addEventListener('product:variant-change', onVariantEvent);

      /* the theme editor, section morphing and GemPages re-renders */
      document.addEventListener('shopify:section:load', function (e) {
        hydrate((e && e.target) || document);
        observeAll();
        touched = null;
        scheduleRead();
      });
      document.addEventListener('shopify:section:unload', function () { prune(); });
      document.addEventListener('shopify:block:select', function (e) {
        hydrate((e && e.target) || document);
        scheduleRead();
      });

      /* the desktop/mobile buy box that is rendered can change without any
         DOM mutation at all */
      window.addEventListener('resize', scheduleRead);
      window.addEventListener('pageshow', scheduleRead);

      run();
    } catch (e) {
      /* nothing above may throw out of the boot sequence */
    }
  }

  window.OblivionVD = {
    version: VERSION,
    refresh: function () {
      try {
        hydrateCart(document);
        if (PRODUCT_MODE) {
          hydrate(document);
          observeAll();
          touched = null;
          variantId = readVariantFromDom() || variantId;
          run();
        }
      } catch (e) { /* ignore */ }
      return this.state();
    },
    /* a copy: the caller can read the numbers but cannot reach into them */
    state: function () {
      return {
        version: VERSION,
        productMode: PRODUCT_MODE,
        cartPrompts: qsa('[data-ob-vd-cart]').length,
        cartBusy: cartBusy,
        tiers: TIERS.length,
        productId: state.productId,
        productHandle: state.productHandle,
        variantId: state.variantId,
        variantKnown: state.variantKnown,
        available: state.available,
        quantity: state.quantity,
        unitBefore: state.unitBefore,
        unitAfter: state.unitAfter,
        lineBefore: state.lineBefore,
        lineAfter: state.lineAfter,
        saved: state.saved,
        tier: state.tier ? {
          minQuantity: state.tier.minQuantity,
          discountType: state.tier.discountType,
          discountValue: state.tier.discountValue,
          label: state.tier.label,
          badge: state.tier.badge
        } : null,
        nextTier: state.nextTier ? {
          minQuantity: state.nextTier.minQuantity,
          label: state.nextTier.label
        } : null,
        needed: state.needed,
        atBest: state.atBest,
        valid: state.valid,
        instances: instances.length,
        quantitySources: candidates.length
      };
    }
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
